import { Activity } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Select,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import { useReadingAlerts } from '@/features/home-readings/api';
import {
  ALERT_STATUS_LABEL,
  ALERT_STATUS_TONE,
  KIND_LABEL,
  OUT_OF_RANGE_LABEL,
  readingValue,
} from '@/features/home-readings/format';
import type { AlertStatusFilter } from '@/features/home-readings/types';
import { ReadingAlertDrawer } from './ReadingAlertDrawer';
import { when } from './rx-request-format';
import styles from './HomeReadings.module.css';

const STATUS_OPTIONS: { value: AlertStatusFilter; label: string }[] = [
  { value: 'active', label: 'Open and acknowledged' },
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
];
const SCOPE_OPTIONS = [
  { value: 'branch', label: 'Whole branch care team' },
  { value: 'mine', label: 'Routed to me' },
];

/**
 * "Reading alerts" (RL-1141): out-of-range home readings (RL-997) for the
 * branch care team. Each alert is routed to the care plan's owner doctor
 * (else the doctor who last saved the plan, else the branch care team);
 * "Routed to me" is the API's `mine=true`.
 */
export function ReadingAlertsTab({ canManage }: { canManage: boolean }) {
  const { selected, canSelectAll } = useBranch();
  const [status, setStatus] = useState<AlertStatusFilter>('active');
  const [scope, setScope] = useState<'branch' | 'mine'>('branch');
  const [openId, setOpenId] = useState<string | null>(null);
  const q = useReadingAlerts(status, scope === 'mine');
  const noBranch = !selected && !canSelectAll;
  const alerts = q.data?.alerts;
  const open = alerts?.find((a) => a.id === openId) ?? null;

  return (
    <Card
      title="Reading alerts"
      flush
      actions={
        <div className={styles.filters}>
          <Field label="Status">
            <Select
              value={status}
              options={STATUS_OPTIONS}
              onChange={(e) => setStatus(e.target.value as AlertStatusFilter)}
            />
          </Field>
          <Field label="Show">
            <Select
              value={scope}
              options={SCOPE_OPTIONS}
              onChange={(e) => setScope(e.target.value as 'branch' | 'mine')}
            />
          </Field>
        </div>
      }
    >
      <DataTable
        caption="Out-of-range home readings"
        rows={noBranch ? [] : alerts}
        rowKey={(a) => a.id}
        loading={!noBranch && q.isLoading}
        error={noBranch ? undefined : q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            icon={<Activity size={22} />}
            title={
              noBranch
                ? 'Choose a branch to see its reading alerts'
                : status === 'active'
                  ? 'No reading alerts waiting'
                  : 'No alerts match this filter'
            }
            body={
              !noBranch && status === 'active'
                ? 'A home blood pressure or glucose reading outside the patient’s alert thresholds appears here.'
                : undefined
            }
          />
        }
        columns={[
          {
            key: 'patient',
            header: 'Patient',
            render: (a) => <strong>{a.patient.fullName}</strong>,
          },
          {
            key: 'reading',
            header: 'Latest reading',
            render: (a) =>
              a.latestReading ? (
                <span className={styles.stack}>
                  <span>{readingValue(a.latestReading)}</span>
                  <span className={styles.muted}>
                    {KIND_LABEL[a.kind]}
                    {a.latestReading.outOfRange
                      ? ` · ${OUT_OF_RANGE_LABEL[a.latestReading.outOfRange]}`
                      : ''}
                  </span>
                </span>
              ) : (
                KIND_LABEL[a.kind]
              ),
          },
          {
            key: 'count',
            header: 'Readings',
            align: 'right',
            render: (a) => a.readingCount,
          },
          {
            key: 'last',
            header: 'Last reading',
            render: (a) => when(a.lastReadingAt),
          },
          {
            key: 'doctor',
            header: 'Routed to',
            render: (a) => a.doctor?.fullName ?? 'Branch care team',
          },
          {
            key: 'status',
            header: 'Status',
            render: (a) => (
              <Badge tone={ALERT_STATUS_TONE[a.status]} dot>
                {ALERT_STATUS_LABEL[a.status]}
              </Badge>
            ),
          },
          {
            key: 'open',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (a) => (
              <Button
                size="sm"
                onClick={() => setOpenId(a.id)}
                aria-label={`Open the reading alert for ${a.patient.fullName}`}
              >
                Open
              </Button>
            ),
          },
        ]}
      />
      {q.data?.hasMore && (
        <p className={styles.cardFoot}>
          Showing the newest 50 alerts. Narrow the status filter to see older
          ones.
        </p>
      )}
      {open && (
        <ReadingAlertDrawer
          alert={open}
          canManage={canManage}
          onClose={() => setOpenId(null)}
        />
      )}
    </Card>
  );
}
